import { useCallback, useEffect, useMemo, useRef, useState } from "react";

const defaultSortValue = (value) => {
  if (value == null) return "";
  if (typeof value === "number") return value;
  return String(value).toLowerCase();
};

const EMPTY_OBJ = Object.freeze({});
const EMPTY_ARR = Object.freeze([]);

const arraysEqual = (a = [], b = []) =>
  a.length === b.length && a.every((item, index) => item === b[index]);

/**
 * Column visibility (persisted), substring filters on display values, and client-side sort.
 * Used only through {@link ConfiguredDataTable}.
 *
 * @param {object} config - columnSequence, columns, lockedColumns, defaultVisibleColumns,
 *   sessionStorageKey + storageType ("local"|"session"), keyAliases, optional defaultSort.
 * @param {(row: object, columnKey: string) => unknown} getCellDisplayValue
 */
export const useConfiguredTable = ({
  config = {},
  headers = {},
  rows = [],
  getCellDisplayValue,
  getCellSortValue,
  onVisibleColumnsChange,
}) => {
  const configColumns = config.columns ?? EMPTY_OBJ;
  const keyAliases = config.keyAliases ?? EMPTY_OBJ;
  const sequence = Array.isArray(config.columnSequence)
    ? config.columnSequence
    : EMPTY_ARR;
  const lockedColumns = Array.isArray(config.lockedColumns)
    ? config.lockedColumns
    : EMPTY_ARR;
  const defaultVisibleColumns = Array.isArray(config.defaultVisibleColumns)
    ? config.defaultVisibleColumns
    : EMPTY_ARR;
  const storageKey = config.sessionStorageKey;
  const storageType = config.storageType || "session";
  const filtersStorageKey = storageKey ? `${storageKey}.filters` : null;

  const getStorage = useCallback(
    () => (storageType === "local" ? localStorage : sessionStorage),
    [storageType]
  );

  const [persistedKeys] = useState(() => {
    if (!storageKey) return [];
    try {
      const storage = getStorage();
      const savedRaw = storage.getItem(storageKey);
      const parsed = savedRaw ? JSON.parse(savedRaw) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
      return [];
    }
  });

  const [visibleColumnKeys, setVisibleColumnKeys] = useState([]);
  const [filters, setFilters] = useState(() => {
    if (!filtersStorageKey) return {};
    try {
      const storage = getStorage();
      const raw = storage.getItem(filtersStorageKey);
      const parsed = raw ? JSON.parse(raw) : {};
      if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
        return parsed;
      }
      return {};
    } catch (error) {
      return {};
    }
  });
  const [orderBy, setOrderBy] = useState(config.defaultSort?.key || null);
  const [order, setOrder] = useState(config.defaultSort?.order || "asc");
  const hasHydratedRef = useRef(false);
  const savedSelectionRef = useRef(null);
  const userModifiedRef = useRef(false);

  const availableColumnKeys = useMemo(() => {
    const knownKeys = Object.keys(headers);
    const configuredKeys = Object.keys(configColumns);
    const relevantPersistedKeys = persistedKeys.filter(
      (key) =>
        knownKeys.includes(key) ||
        Object.prototype.hasOwnProperty.call(configColumns, key)
    );
    const merged = Array.from(
      new Set([...configuredKeys, ...knownKeys, ...relevantPersistedKeys])
    );

    const resolvedSequence = sequence.map((key) => {
      const aliases = keyAliases[key] || [];
      const resolved = [key, ...aliases].find((candidate) =>
        merged.includes(candidate)
      );
      return resolved || key;
    });

    const ordered = resolvedSequence
      .filter((key) => merged.includes(key))
      .filter((key) => key !== "actions");
    const remaining = merged
      .filter((key) => !ordered.includes(key) && key !== "actions")
      .sort((a, b) => a.localeCompare(b));

    if (merged.includes("actions")) {
      return [...ordered, ...remaining, "actions"];
    }
    return [...ordered, ...remaining];
  }, [configColumns, headers, keyAliases, persistedKeys, sequence]);

  useEffect(() => {
    // Hydrate persisted selection once; later renders should derive from this baseline.
    if (!hasHydratedRef.current) {
      if (storageKey) {
        const storage = getStorage();
        const savedRaw = storage.getItem(storageKey);
        if (savedRaw) {
          try {
            const parsed = JSON.parse(savedRaw);
            if (Array.isArray(parsed)) {
              savedSelectionRef.current = parsed;
            }
          } catch (error) {
            savedSelectionRef.current = null;
          }
        }
      }
      hasHydratedRef.current = true;
    }

    const resolveDefaultKey = (key) => {
      if (availableColumnKeys.includes(key)) return key;
      const candidate = (keyAliases[key] || []).find((alias) =>
        availableColumnKeys.includes(alias)
      );
      return candidate || null;
    };

    const defaults = defaultVisibleColumns.map(resolveDefaultKey).filter(Boolean);
    const lockedVisible = lockedColumns.filter((key) =>
      availableColumnKeys.includes(key)
    );
    // Prefer persisted user choice when available, otherwise fallback to configured defaults.
    let baseSelection = Array.isArray(savedSelectionRef.current)
      ? savedSelectionRef.current
      : defaults;

    // Feedback / Device Status: if persisted selection is empty or only locked, fall back to defaults on refresh.
    if (
      (storageKey === "feedback.visibleColumns" ||
        storageKey === "deviceStatus.visibleColumns" ||
        String(storageKey || "").startsWith("deviceStatus.visibleColumns.")) &&
      (baseSelection.length === 0 ||
        baseSelection.every((key) => lockedColumns.includes(key)))
    ) {
      baseSelection = defaults;
    }

    let nextVisible = Array.from(
      new Set([
        ...baseSelection.filter((key) => availableColumnKeys.includes(key)),
        ...lockedVisible,
      ])
    );

    if (nextVisible.length === 0 && availableColumnKeys.length > 0) {
      nextVisible = [availableColumnKeys[0], ...lockedVisible];
    }

    setVisibleColumnKeys((prev) =>
      arraysEqual(prev, nextVisible) ? prev : Array.from(new Set(nextVisible))
    );
  }, [
    availableColumnKeys,
    defaultVisibleColumns,
    getStorage,
    keyAliases,
    lockedColumns,
    storageKey,
  ]);

  useEffect(() => {
    if (visibleColumnKeys.length === 0) return;
    if (storageKey) {
      const storage = getStorage();
      const pendingSavedKeys = Array.isArray(savedSelectionRef.current)
        ? savedSelectionRef.current.filter(
            (key) => !availableColumnKeys.includes(key)
          )
        : [];

      // Avoid overwriting persisted wide selections while dynamic keys are still loading.
      if (pendingSavedKeys.length > 0 && !userModifiedRef.current) {
        onVisibleColumnsChange?.(visibleColumnKeys);
        return;
      }

      savedSelectionRef.current = visibleColumnKeys;
      storage.setItem(storageKey, JSON.stringify(visibleColumnKeys));
    }
    onVisibleColumnsChange?.(visibleColumnKeys);
  }, [
    availableColumnKeys,
    getStorage,
    onVisibleColumnsChange,
    storageKey,
    visibleColumnKeys,
  ]);

  const allColumns = useMemo(
    () =>
      availableColumnKeys.map((key) => {
        const meta = configColumns[key] || {};
        return {
          key,
          label: meta.label || headers[key]?.header || key,
          sortable: Boolean(meta.sortable),
          filterable: Boolean(meta.filterable),
          locked: lockedColumns.includes(key),
          align: meta.align || "left",
          justifyContent: meta.justifyContent || "flex-start",
          minWidth: meta.minWidth,
          headerCellSx: meta.headerCellSx || {},
          cellSx: meta.cellSx || {},
        };
      }),
    [availableColumnKeys, configColumns, headers, lockedColumns]
  );

  const visibleColumns = useMemo(
    () => allColumns.filter((column) => visibleColumnKeys.includes(column.key)),
    [allColumns, visibleColumnKeys]
  );

  const filterableColumns = useMemo(
    () => allColumns.filter((column) => column.filterable),
    [allColumns]
  );

  const filteredRows = useMemo(() => {
    const activeFilters = Object.entries(filters).filter(
      ([, filterVal]) => String(filterVal || "").trim() !== ""
    );
    if (activeFilters.length === 0) return rows;
    return rows.filter((row) =>
      activeFilters.every(([key, filterValue]) => {
        const value = String(getCellDisplayValue(row, key) || "").toLowerCase();
        return value.includes(String(filterValue).toLowerCase().trim());
      })
    );
  }, [filters, getCellDisplayValue, rows]);

  const sortedRows = useMemo(() => {
    if (!orderBy) return filteredRows;
    const sortableColumn = allColumns.find(
      (column) => column.key === orderBy && column.sortable
    );
    if (!sortableColumn) return filteredRows;

    const direction = order === "asc" ? 1 : -1;
    return [...filteredRows].sort((a, b) => {
      const av = getCellSortValue
        ? getCellSortValue(a, orderBy)
        : defaultSortValue(getCellDisplayValue(a, orderBy));
      const bv = getCellSortValue
        ? getCellSortValue(b, orderBy)
        : defaultSortValue(getCellDisplayValue(b, orderBy));
      if (av < bv) return -1 * direction;
      if (av > bv) return 1 * direction;
      return 0;
    });
  }, [allColumns, filteredRows, getCellDisplayValue, getCellSortValue, order, orderBy]);

  const handleSort = (columnKey) => {
    if (orderBy === columnKey) {
      setOrder((prev) => (prev === "asc" ? "desc" : "asc"));
      return;
    }
    setOrderBy(columnKey);
    setOrder("asc");
  };

  const toggleColumn = (key) => {
    if (lockedColumns.includes(key)) return;
    // Mark explicit user interaction so subsequent persistence can safely overwrite defaults.
    userModifiedRef.current = true;
    setVisibleColumnKeys((prev) => {
      const exists = prev.includes(key);
      const next = exists ? prev.filter((k) => k !== key) : [...prev, key];
      const lockedVisible = lockedColumns.filter((k) =>
        availableColumnKeys.includes(k)
      );
      return Array.from(new Set([...next, ...lockedVisible]));
    });
  };

  const resetVisibleColumns = () => {
    userModifiedRef.current = true;
    const resolveDefaultKey = (key) => {
      if (availableColumnKeys.includes(key)) return key;
      const candidate = (keyAliases[key] || []).find((alias) =>
        availableColumnKeys.includes(alias)
      );
      return candidate || null;
    };
    const defaults = defaultVisibleColumns.map(resolveDefaultKey).filter(Boolean);
    const lockedVisible = lockedColumns.filter((key) =>
      availableColumnKeys.includes(key)
    );
    setVisibleColumnKeys(Array.from(new Set([...defaults, ...lockedVisible])));
  };

  const clearFilters = () => setFilters({});

  const selectAllColumns = () => {
    userModifiedRef.current = true;
    setVisibleColumnKeys(allColumns.map((column) => column.key));
  };

  const deselectAllColumns = () => {
    userModifiedRef.current = true;
    const lockedVisible = lockedColumns.filter((key) =>
      availableColumnKeys.includes(key)
    );
    setVisibleColumnKeys(Array.from(new Set(lockedVisible)));
  };

  const applyVisibleColumns = (nextKeys = []) => {
    userModifiedRef.current = true;
    const lockedVisible = lockedColumns.filter((key) =>
      availableColumnKeys.includes(key)
    );
    const sanitized = Array.from(
      new Set([
        ...nextKeys.filter((key) => availableColumnKeys.includes(key)),
        ...lockedVisible,
      ])
    );
    setVisibleColumnKeys(sanitized);
  };

  useEffect(() => {
    if (!filtersStorageKey) return;
    try {
      const storage = getStorage();
      storage.setItem(filtersStorageKey, JSON.stringify(filters));
    } catch (error) {
      // Swallow persistence errors to avoid impacting table UX.
    }
  }, [filters, filtersStorageKey, getStorage]);

  return {
    allColumns,
    visibleColumns,
    visibleColumnKeys,
    filterableColumns,
    filters,
    setFilters,
    filteredRows,
    sortedRows,
    orderBy,
    order,
    handleSort,
    toggleColumn,
    selectAllColumns,
    deselectAllColumns,
    resetVisibleColumns,
    clearFilters,
    applyVisibleColumns,
    defaultVisibleColumns,
  };
};
